import { Suspense, type ReactNode, useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import LoadingState from "@/components/shared/LoadingState";
import { RouteRuntimeBoundary, type RuntimeSurfaceContext } from "@/components/shared/RuntimeSurfaceBoundary";
import { cleanupChunkRecoveryUrl, lazyWithRetry } from "@/lib/lazy-with-retry";
import { AuthProvider } from "@/contexts/AuthContext";
import { ConsentProvider } from "@/contexts/ConsentProvider";
import { usePageTracking } from "@/hooks/usePageTracking";
import AppToastLayer from "./components/app/AppToastLayer";
import MobileAppBridge from "./components/mobile/MobileAppBridge";
import { publicConfigStatus } from "@/lib/public-config";

const LandingPage = lazyWithRetry(() => import("./pages/MatchLandingPage"), "public-landing");
const LegalDocumentPage = lazyWithRetry(() => import("./pages/LegalDocumentPage"), "legal-document");
const DataSubjectRequestPage = lazyWithRetry(
  () => import("./pages/DataSubjectRequestPage"),
  "data-subject-request",
);
const AccountPrivacyPage = lazyWithRetry(() => import("./pages/AccountPrivacyPage"), "account-privacy");
const NotFound = lazyWithRetry(() => import("./pages/NotFound"), "not-found");
const PublicCourtPage = lazyWithRetry(() => import("./pages/PublicCourtPage"), "public-court");
const PublicPartnerPage = lazyWithRetry(() => import("./pages/PublicPartnerPage"), "public-partner");
const PublicVenuePage = lazyWithRetry(() => import("./pages/PublicVenuePage"), "public-venue");
const SessionRoute = lazyWithRetry(() => import("./routes/SessionRoute"), "session-route");
const AppRouteShell = lazyWithRetry(() => import("./routes/AppRouteShell"), "app-route-shell");

function isAuthError(error: unknown) {
  return (
    error instanceof Error &&
    ["not_authenticated", "missing_auth", "invalid_auth"].includes(error.message)
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        if (isAuthError(error)) {
          return false;
        }

        return failureCount < 2;
      },
    },
    mutations: {
      retry: (failureCount, error) => {
        if (isAuthError(error)) {
          return false;
        }

        return failureCount < 1;
      },
    },
  },
});

function RouteLoadingFallback() {
  return (
    <div className="flex min-h-[42vh] items-center justify-center">
      <div className="w-full max-w-4xl">
        <LoadingState variant="cards" count={3} />
      </div>
    </div>
  );
}

function withSuspense(element: ReactNode) {
  return <Suspense fallback={<RouteLoadingFallback />}>{element}</Suspense>;
}

function withSurface(element: ReactNode, context: RuntimeSurfaceContext) {
  return <RouteRuntimeBoundary context={context}>{withSuspense(element)}</RouteRuntimeBoundary>;
}

function ChunkRecoveryCleanup() {
  useEffect(() => {
    cleanupChunkRecoveryUrl(window.location.href);
  }, []);

  return null;
}

function RouteAnalytics() {
  usePageTracking();
  return null;
}

function PublicConfigErrorPage() {
  return (
    <main className="flex min-h-screen items-center justify-center bg-[#f8fbf9] px-6 py-12">
      <section className="w-full max-w-xl rounded-[28px] border border-red-100 bg-white p-6 shadow-[0_18px_45px_-32px_rgba(11,23,17,0.4)]">
        <p className="text-xs font-black uppercase tracking-[0.16em] text-red-600">
          Configuracao incompleta
        </p>
        <h1 className="mt-3 text-2xl font-black tracking-[-0.04em] text-[#101d16]">
          O Match nao conseguiu iniciar com seguranca.
        </h1>
        <p className="mt-3 text-sm leading-6 text-[#53645b]">
          O ambiente de producao esta sem variaveis publicas obrigatorias. Corrija a configuracao
          do deploy e publique novamente para evitar conexao com um projeto incorreto.
        </p>
        <div className="mt-5 rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
          Variaveis pendentes: {publicConfigStatus.missingKeys.join(", ")}
        </div>
      </section>
    </main>
  );
}

const App = () => {
  if (!publicConfigStatus.isValid) {
    return <PublicConfigErrorPage />;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <ChunkRecoveryCleanup />
      <AppToastLayer />
      <ConsentProvider>
        <AuthProvider>
          <BrowserRouter>
            <RouteAnalytics />
            <MobileAppBridge />
            <Routes>
              <Route path="/" element={withSurface(<LandingPage />, "public")} />
              <Route path="/m/:venueSlug" element={withSurface(<PublicVenuePage />, "public")} />
              <Route path="/p/:partnerSlug" element={withSurface(<PublicPartnerPage />, "public")} />
              <Route path="/q/:venueSlug/:courtSlug" element={withSurface(<PublicCourtPage />, "public")} />
              <Route path="/termos" element={withSurface(<LegalDocumentPage />, "public")} />
              <Route path="/privacidade" element={withSurface(<LegalDocumentPage />, "public")} />
              <Route path="/privacidade/cookies" element={withSurface(<LegalDocumentPage />, "public")} />
              <Route path="/privacidade/pedido-titular" element={withSurface(<DataSubjectRequestPage />, "public")} />
              <Route path="/cancelamento" element={withSurface(<LegalDocumentPage />, "public")} />
              <Route path="/conta/privacidade" element={withSurface(<AccountPrivacyPage />, "public")} />
              <Route path="/auth" element={withSurface(<SessionRoute page="auth" />, "session")} />
              <Route
                path="/forgot-password"
                element={withSurface(<SessionRoute page="forgot-password" />, "session")}
              />
              <Route
                path="/reset-password"
                element={withSurface(<SessionRoute page="reset-password" />, "session")}
              />
              <Route path="/app/*" element={withSurface(<AppRouteShell />, "app")} />
              <Route path="*" element={withSurface(<NotFound />, "public")} />
            </Routes>
          </BrowserRouter>
        </AuthProvider>
      </ConsentProvider>
    </QueryClientProvider>
  );
};

export default App;
